import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { Brain, Trophy, Clock, Target, TrendingUp, ArrowUpCircle, ArrowDownCircle, AlertCircle } from 'lucide-react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { loadUserProgress } from './progressManager';

const KPICard = ({ icon: Icon, title, value, change, color }) => (
  <div 
    className="bg-white rounded-xl shadow-sm p-6 border-l-4 h-full" 
    style={{ borderLeftColor: color }}
  >
    <div className="flex items-start justify-between">
      <div>
        <h3 className="text-gray-600 text-sm font-medium">{title}</h3>
        <p className="text-2xl font-bold mt-1">{value}</p>
      </div>
      <div className="rounded-lg p-3" style={{ backgroundColor: `${color}15` }}>
        <Icon className="w-6 h-6" style={{ color: color }} />
      </div>
    </div>
    {change && (
      <div className="flex items-center gap-1 mt-2">
        {change >= 0 ? (
          <ArrowUpCircle className="w-4 h-4 text-green-500" />
        ) : (
          <ArrowDownCircle className="w-4 h-4 text-red-500" />
        )}
        <span className={`text-sm ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {change >= 0 ? '+' : ''}{change}% vs période précédente
        </span>
      </div>
    )}
  </div>
);

const Dashboard = ({ userId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedTheme, setSelectedTheme] = useState('all');
  const [selectedDomain, setSelectedDomain] = useState('all');
  const [competences, setCompetences] = useState([]);
  const [progressData, setProgressData] = useState({});

  const objectiveThemes = [
    { id: 'all', label: 'Tous les objectifs' },
    { id: 'Educatif', label: 'Éducatif' },
    { id: 'Professionnel', label: 'Professionnel' },
    { id: 'Personnel', label: 'Personnel' },
    { id: 'Business', label: 'Business' }
  ];

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) {
        setError("UserId non défini");
        setLoading(false);
        return;
      }

      try {
        const competencesCollection = collection(db, 'users', userId, 'competences');
        const competencesSnapshot = await getDocs(competencesCollection);
        const competencesData = competencesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setCompetences(competencesData);

        const progress = await loadUserProgress(userId);
        setProgressData(progress);
        
      } catch (err) {
        setError("Erreur lors du chargement des données: " + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId]);

    // Fonction pour tronquer les titres
  const truncateTitle = (title, maxLength = 40) => {
    if (title.length <= maxLength) return title;
    return `${title.substring(0, maxLength)}...`;
  };

  // Préparation des données pour les graphiques
  const getFilteredChapterData = () => {
    return competences.map(comp => ({
      name: truncateTitle(comp.title),
      fullName: comp.title, // Garder le titre complet pour le tooltip
      performance: progressData[comp.id]?.progress || 0,
      theme: comp.theme,
      domain: comp.chapitre,
      quizStats: {
        correct: progressData[comp.id]?.totalCorrectAnswers || 0,
        total: progressData[comp.id]?.totalQuestionsAnswered || 0
      },
      exercisePoints: progressData[comp.id]?.Points_Exercices || 0
    })).filter(chapter => 
      (selectedTheme === 'all' || chapter.theme === selectedTheme) &&
      (selectedDomain === 'all' || chapter.domain === selectedDomain)
    );
  };

  // Calcul des KPIs globaux avec les filtres
  const calculateGlobalKPIs = () => {
    const filteredData = getFilteredChapterData();

    const totalPerformance = filteredData.reduce((sum, chapter) => sum + chapter.performance, 0);
    const averagePerformance = filteredData.length > 0 
      ? Math.round(totalPerformance / filteredData.length) 
      : 0;

    const totalPoints = filteredData.reduce((sum, chapter) => 
      sum + (chapter.exercisePoints || 0) + ((chapter.quizStats.correct || 0) * 50), 0);

    const totalChapters = filteredData.length;
    const masteredChapters = filteredData.filter(chapter => chapter.performance >= 75).length;

    return {
      performance: `${averagePerformance}%`,
      points: totalPoints.toLocaleString(),
      masteredChapters: `${masteredChapters}/${totalChapters}`
    };
  };

  const filteredChapterData = getFilteredChapterData();
  const globalKPIs = calculateGlobalKPIs();

  // Obtenir les domaines disponibles en fonction du thème sélectionné
  const availableDomains = [...new Set(
    competences
      .filter(comp => selectedTheme === 'all' || comp.theme === selectedTheme)
      .map(comp => comp.chapitre)
  )];

  const getPerformanceColor = (performance) => {
    if (performance >= 80) return '#22c55e';
    if (performance >= 60) return '#3498db';
    if (performance >= 40) return '#f59e0b';
    return '#ef4444';
  };

  if (loading) {
    return <div>Chargement...</div>;
  }

  if (error) {
    return <div>Erreur: {error}</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* En-tête et filtres */}
      <div className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="max-w-[2000px] mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <h1 className="text-2xl font-bold text-gray-800">Tableau de bord</h1>
            
            <div className="flex flex-wrap gap-3">
              <select 
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="rounded-lg border border-gray-200 px-4 py-2 bg-white text-gray-600 focus:ring-2 focus:ring-blue-500"
              >
                <option value="week">Cette semaine</option>
                <option value="month">Ce mois</option>
                <option value="year">Cette année</option>
              </select>
              
              <select
                value={selectedTheme}
                onChange={(e) => {
                  setSelectedTheme(e.target.value);
                  setSelectedDomain('all'); // Réinitialiser le domaine lors du changement de thème
                }}
                className="rounded-lg border border-gray-200 px-4 py-2 bg-white text-gray-600 focus:ring-2 focus:ring-blue-500"
              >
                {objectiveThemes.map(type => (
                  <option key={type.id} value={type.id}>{type.label}</option>
                ))}
              </select>

              <select
                value={selectedDomain}
                onChange={(e) => setSelectedDomain(e.target.value)}
                className="rounded-lg border border-gray-200 px-4 py-2 bg-white text-gray-600 focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Tous les domaines</option>
                {availableDomains.map(domain => (
                  <option key={domain} value={domain}>{domain}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="max-w-[2000px] mx-auto px-4 py-6">
        <div className="space-y-6">

          {/* KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <KPICard
                  icon={TrendingUp}
                  title="Progression globale"
                  value={globalKPIs.performance}
                  change={5}
                  color="#3498db"
                />
              </div>
              <div>
                <KPICard
                  icon={Trophy}
                  title="Points totaux"
                  value={globalKPIs.points}
                  change={3}
                  color="#2ecc71"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <KPICard
                  icon={Clock}
                  title="Temps d'étude"
                  value="24h"
                  change={-2}
                  color="#9b59b6"
                />
              </div>
              <div>
                <KPICard
                  icon={Target}
                  title="Objectifs atteints"
                  value={globalKPIs.masteredChapters}
                  change={8}
                  color="#f1c40f"
                />
              </div>
            </div>
          </div>

          {/* Graphique des performances */}
          <div className="w-full">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex flex-col space-y-4">
                <h2 className="text-lg font-semibold">Progression par objectif</h2>
                <div className="text-sm text-gray-500">
                  Progression détaillée pour chaque objectif
                </div>
                <div className="h-[600px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={filteredChapterData}
                      layout="vertical"
                      margin={{ left: 250, right: 30, top: 10, bottom: 10 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        type="number" 
                        domain={[0, 100]} 
                        tickFormatter={(value) => `${value}%`}
                      />
                      <YAxis 
                        dataKey="name" 
                        type="category" 
                        width={250}
                        tick={{ fontSize: 14 }}
                      />
                      <Tooltip 
                        formatter={(value) => [`${value}%`, 'Progression']}
                        labelFormatter={(label) => {
                          const chapter = filteredChapterData.find(item => item.name === label);
                          return `Objectif : ${chapter?.fullName || label}`;
                        }}
                        contentStyle={{ fontSize: 14 }}
                      />
                      <Legend />
                      <Bar
                        dataKey="performance"
                        name="Performance"
                        radius={[0, 4, 4, 0]}
                      >
                        {filteredChapterData.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`}
                            fill={getPerformanceColor(entry.performance)}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>

          {/* Recommandations */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold mb-6 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-500" />
              Recommandations prioritaires
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {filteredChapterData
                .sort((a, b) => a.performance - b.performance)
                .slice(0, 3)
                .map((item, index) => (
                  <div 
                    key={index}
                    className="p-4 rounded-lg border-l-4 bg-orange-50"
                    style={{ borderLeftColor: getPerformanceColor(item.performance) }}
                  >
                    <div className="flex items-start gap-3">
                      <Brain className="w-5 h-5" style={{ color: getPerformanceColor(item.performance) }} />
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-800">
                          {item.name} <span className="text-sm text-gray-500">({item.domain})</span>
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                          Performance actuelle : {item.performance}%
                        </p>
                        <div className="mt-2 flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-gray-200 rounded-full">
                            <div
                              className="h-1.5 rounded-full"
                              style={{
                                width: `${item.performance}%`,
                                backgroundColor: getPerformanceColor(item.performance)
                              }}
                            />
                          </div>
                          <span className="text-xs font-medium" style={{ color: getPerformanceColor(item.performance) }}>
                            Priorité {index + 1}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
              ))} 
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;